import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @fileoverview FollowingRow — LOCI following list row.
 *
 * 40×40 gradient avatar on the left, handle in Space Grotesk in the middle,
 * mono follower-count caption on the right. Rendered as a full-row `<a>` so
 * Enter activates the link natively and screen readers announce the target.
 */
import { forwardRef } from 'react';
import { useTheme } from '../../../theme';
import { brandColors } from '../../../theme/design-tokens';
const AVATAR_SIZE = 40;
function formatFollowers(count) {
    if (!Number.isFinite(count) || count < 0)
        return '0';
    if (count < 1000)
        return count.toString();
    if (count < 1000000) {
        const k = count / 1000;
        return `${k >= 10 ? Math.round(k) : k.toFixed(1).replace(/\.0$/, '')}k`;
    }
    const m = count / 1000000;
    return `${m >= 10 ? Math.round(m) : m.toFixed(1).replace(/\.0$/, '')}m`;
}
function deriveInitials(handle) {
    const trimmed = handle.replace(/^@+/, '').trim();
    if (!trimmed)
        return '?';
    return trimmed[0].toUpperCase();
}
/**
 * `<FollowingRow>` — anchor row used inside the Profile `FOLLOWING` list.
 *
 * Uses a native `<a>` root so Enter follows the link and keyboard focus is
 * delivered by user-agent default. The avatar renders as a decorative span
 * (image content is already conveyed by the handle label).
 */
export const FollowingRow = forwardRef(({ id, handle, avatarUrl, followersCount, href, className, style, 'data-testid': dataTestId = 'following-row', }, ref) => {
    const theme = useTheme();
    const displayHandle = handle.startsWith('@') ? handle : `@${handle}`;
    const followersLabel = formatFollowers(followersCount);
    const rowStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing.md,
        width: '100%',
        padding: `${theme.spacing.sm} ${theme.spacing.xs}`,
        borderRadius: theme.borderRadius.sm,
        color: 'inherit',
        textDecoration: 'none',
        font: 'inherit',
    };
    const avatarStyle = {
        width: AVATAR_SIZE,
        height: AVATAR_SIZE,
        flexShrink: 0,
        borderRadius: '50%',
        background: avatarUrl
            ? `center / cover no-repeat url(${JSON.stringify(avatarUrl)})`
            : brandColors.gradient.sunset,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#FFFFFF',
        fontFamily: theme.typography.fontFamilyDisplay ?? theme.typography.fontFamily,
        fontSize: theme.typography.fontSize.small,
        fontWeight: theme.typography.fontWeight.semibold,
        letterSpacing: '-0.01em',
    };
    const bodyStyle = {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
    };
    const handleStyle = {
        margin: 0,
        fontFamily: theme.typography.fontFamilyDisplay ?? theme.typography.fontFamily,
        fontSize: theme.typography.fontSize.body,
        fontWeight: theme.typography.fontWeight.semibold,
        color: theme.colors.text,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        letterSpacing: '-0.005em',
    };
    const followersStyle = {
        margin: 0,
        fontFamily: theme.typography.fontFamilyMono,
        fontSize: '12px',
        letterSpacing: '0.04em',
        color: theme.colors.textMuted,
        fontFeatureSettings: "'tnum' 1, 'zero' 1",
    };
    return (_jsxs("a", { ref: ref, href: href, "data-testid": dataTestId, "data-user-id": id, "aria-label": `Open profile for ${displayHandle}`, className: className, style: { ...rowStyle, ...style }, children: [_jsx("span", { "aria-hidden": "true", style: avatarStyle, "data-testid": "following-row-avatar", children: avatarUrl ? null : deriveInitials(handle) }), _jsxs("span", { style: bodyStyle, children: [_jsx("span", { style: handleStyle, "data-testid": "following-row-handle", children: displayHandle }), _jsxs("span", { style: followersStyle, "data-testid": "following-row-followers", children: [followersLabel, " followers"] })] })] }));
});
FollowingRow.displayName = 'FollowingRow';
